/**
 * Scenario picker — sibling of EngineInputScopePicker (same popover pattern,
 * same tokens). Replaces the native <select>+optgroup, which rendered as an
 * unstyleable OS menu.
 *
 * The cycle binding is an attribute of the scenario, so it lives INSIDE this
 * control: a fixed-height sub-line on the trigger and group headers in the
 * popover. Nothing outside the control moves when binding state changes.
 *
 * Empty / loading states keep the same solid trigger as Input Scope — never
 * swap the control for a dashed "Create" link (that made the × separator look
 * like a stray clear button). Create lives as a footer action in the popover.
 *
 * Semantic accents (design system): cycle binding = structure (primary/slate),
 * what-if = mustard (amber), closed cycle = coral (red). Eyebrows are mono.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, Layers, Loader2, Plus, Search } from 'lucide-react';
import { usePlanningEngine } from './PlanningEngineContext';
import { activateScenario } from '../../../lib/planningEngineService';
import { listPlanCycles } from '../../../lib/planningService';
import { isPlanCycleCommitTarget, type PlanCycleStatus } from '../../../types/planning';
import {
  deriveScenarioLane,
  SCENARIO_LANE_BADGE_CLASS,
  SCENARIO_LANE_LABEL,
} from '../../../lib/planningScenarioLanes';
import type { Scenario } from '../../../lib/planningEngineModule.types';
import {
  filterGroupedScenarios,
  groupScenariosByCycle,
  scenarioDupeDateLabels,
} from './engineScenarioPickerModel';

interface CycleInfo {
  name: string;
  status: PlanCycleStatus;
}

function BindingSubline({
  scenario,
  cycles,
  loading,
}: {
  scenario: Scenario | null;
  cycles: Map<string, CycleInfo>;
  loading: boolean;
}) {
  const base = 'font-mono text-[10px] uppercase tracking-wider truncate leading-4';
  if (loading && !scenario) {
    return <span className={`${base} text-[var(--text-ter)] dark:text-[var(--text-ter)]`}>Loading…</span>;
  }
  if (!scenario) {
    return (
      <span className={`${base} text-[var(--text-ter)] dark:text-[var(--text-ter)]`}>
        Pick existing or create new
      </span>
    );
  }
  if (!scenario.planCycleId) {
    return (
      <span className={`${base} text-[var(--mustard)] dark:text-[var(--mustard)]`}>What-if · no cycle</span>
    );
  }
  const cycle = cycles.get(scenario.planCycleId);
  if (!cycle) {
    return (
      <span
        className={`${base} inline-block w-24 rounded bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] animate-pulse`}
        aria-label="Resolving cycle"
      >
        &nbsp;
      </span>
    );
  }
  const closed = !isPlanCycleCommitTarget(cycle.status);
  return (
    <span
      className={`${base} ${
        closed
          ? 'text-[var(--coral)] dark:text-[var(--coral)]'
          : 'text-primary-600 dark:text-primary-400'
      }`}
      title={
        closed
          ? `Cycle ${cycle.name} is ${cycle.status} — simulation stays open, commits are blocked`
          : `Bound to plan cycle ${cycle.name}`
      }
    >
      Cycle · {cycle.name}
      {closed ? ` · ${cycle.status}` : ''}
    </span>
  );
}

export function EngineScenarioPicker() {
  const {
    projectId,
    scenarios,
    activeScenario,
    setActiveScenario,
    refreshScenarios,
    loading,
  } = usePlanningEngine();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [cycles, setCycles] = useState<Map<string, CycleInfo>>(new Map());
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const anyBound = scenarios.some((s) => s.planCycleId);
  useEffect(() => {
    if (!anyBound) return;
    let cancelled = false;
    void listPlanCycles().then(({ data }) => {
      if (!cancelled) {
        setCycles(new Map(data.map((c) => [c.id, { name: c.name, status: c.status }])));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [anyBound]);

  const grouped = useMemo(() => groupScenariosByCycle(scenarios), [scenarios]);
  const dupeLabels = useMemo(() => scenarioDupeDateLabels(scenarios), [scenarios]);

  // zero/one/few/many: 8+ scenarios gets a search input, non-negotiable.
  const showSearch = scenarios.length >= 8;
  const filteredGrouped = useMemo(
    () => filterGroupedScenarios(grouped, query),
    [grouped, query],
  );
  const noMatches =
    query.trim().length > 0 &&
    filteredGrouped.cycleGroups.length === 0 &&
    filteredGrouped.whatIf.length === 0;

  const openPopover = useCallback(() => {
    setQuery('');
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const popoverH = 380;
      const openUpward = spaceBelow < popoverH && rect.top > popoverH;
      setPopoverStyle({
        position: 'fixed',
        left: Math.min(rect.left, window.innerWidth - 360),
        width: Math.max(rect.width, 340),
        zIndex: 9999,
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + 6 }
          : { top: rect.bottom + 6 }),
      });
    }
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!document.contains(t)) return;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(t) &&
        popoverRef.current &&
        !popoverRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [open]);

  // Position is computed once at open time (fixed to viewport coords) and
  // never recalculated — on scroll the trigger moves with the page while the
  // popover stays put, visually detaching. Close instead of tracking scroll.
  //
  // Capture-phase, because a scroll event does not bubble: this is the only way
  // to see the page scroll under a nested container. The cost of capture is
  // that scrolls originating INSIDE the popover arrive here too — and the
  // popover has its own overflow-y-auto list. Scrolling that list does not move
  // the trigger, so the popover is still anchored correctly and must stay open.
  // Without this guard the control is unusable the moment the list overflows,
  // which is exactly when a list needs scrolling (defect: 2026-09-02 demo).
  useEffect(() => {
    if (!open) return;
    const handler = (e: Event) => {
      const target = e.target as Node | null;
      if (target && popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', handler, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', handler, { capture: true });
  }, [open]);

  const handleSwitch = useCallback(
    async (scenario: Scenario) => {
      if (!projectId || scenario.id === activeScenario?.id) {
        setOpen(false);
        return;
      }
      setSwitching(true);
      setOpen(false);
      try {
        await activateScenario(scenario.id, projectId);
        await refreshScenarios();
        setActiveScenario(scenario);
      } finally {
        setSwitching(false);
      }
    },
    [projectId, activeScenario?.id, refreshScenarios, setActiveScenario],
  );

  const scenarioRow = (s: Scenario) => {
    const lane = deriveScenarioLane(s);
    const isActive = s.id === activeScenario?.id;
    const dupe = dupeLabels.get(s.id);
    return (
      <button
        key={s.id}
        type="button"
        onClick={() => void handleSwitch(s)}
        className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
          isActive
            ? 'bg-primary-50 dark:bg-[var(--active-tint)]'
            : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
        }`}
      >
        <span
          className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-mono uppercase tracking-wide ${SCENARIO_LANE_BADGE_CLASS[lane]}`}
        >
          {SCENARIO_LANE_LABEL[lane]}
        </span>
        <span className="flex-1 min-w-0 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
          {s.name}
        </span>
        {dupe && (
          <span className="shrink-0 font-mono text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            {dupe}
          </span>
        )}
        {isActive && <Check className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />}
      </button>
    );
  };

  const groupEyebrow = (label: string, closed?: boolean) => (
    <div
      className={`px-3 pt-3 pb-1 font-mono text-[10px] uppercase tracking-wider ${
        closed
          ? 'text-[var(--coral)] dark:text-[var(--coral)]'
          : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
      }`}
    >
      {label}
    </div>
  );

  const createFooter = (
    <div className="border-t border-[var(--rule)] dark:border-[var(--rule)] p-2">
      <Link
        to="/planning/engine/scenarios"
        onClick={() => setOpen(false)}
        className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-950/30 transition-colors"
      >
        <Plus className="h-4 w-4 shrink-0" />
        Create a scenario
      </Link>
    </div>
  );

  const popover = open ? (
    <div
      ref={popoverRef}
      style={popoverStyle}
      className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] shadow-2xl overflow-hidden"
    >
      <div className="px-4 pt-4 pb-3 border-b border-[var(--rule)] dark:border-[var(--rule)]">
        <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">
          Scenario
        </p>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">
          Assumptions for this run — pick an existing scenario or create a new one
        </p>
      </div>
      {showSearch && (
        <div className="px-3 pt-2 pb-2 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
            <input
              ref={searchInputRef}
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${scenarios.length} scenarios…`}
              className="w-full pl-8 pr-2.5 py-1.5 text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
          </div>
        </div>
      )}
      <div className="py-1 max-h-[60vh] overflow-y-auto">
        {loading && scenarios.length === 0 && (
          <div className="flex items-center gap-2 px-3 py-4 text-xs text-[var(--text-ter)]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading scenarios…
          </div>
        )}
        {!loading && scenarios.length === 0 && (
          <p className="px-3 py-4 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            No scenarios in this workspace yet. Create one to start a Scenario × Scope instance.
          </p>
        )}
        {noMatches && (
          <p className="px-3 py-4 text-xs text-[var(--text-ter)] italic">
            No scenarios match “{query.trim()}”.
          </p>
        )}
        {filteredGrouped.cycleGroups.map(({ cycleId, scenarios: list }) => {
          const cycle = cycles.get(cycleId);
          const closed = cycle ? !isPlanCycleCommitTarget(cycle.status) : false;
          return (
            <div key={cycleId}>
              {groupEyebrow(
                `Cycle · ${cycle?.name ?? '…'}${closed ? ` · ${cycle?.status}` : ''}`,
                closed,
              )}
              {list.map(scenarioRow)}
            </div>
          );
        })}
        {filteredGrouped.whatIf.length > 0 && (
          <div>
            {filteredGrouped.cycleGroups.length > 0 && groupEyebrow('What-if · no cycle')}
            {filteredGrouped.whatIf.map(scenarioRow)}
          </div>
        )}
      </div>
      {createFooter}
    </div>
  ) : null;

  const triggerLabel = switching
    ? 'Switching…'
    : loading && !activeScenario
      ? 'Loading scenarios…'
      : (activeScenario?.name ?? 'Select scenario…');

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openPopover}
        aria-label="Active scenario"
        aria-expanded={open}
        className={`w-[260px] flex items-center gap-2.5 px-2.5 py-1  border text-left transition-all hover:border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-indigo-200 ${
          open
            ? 'border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 ring-1 ring-indigo-200'
            : 'border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]'
        }`}
      >
        {switching || (loading && !activeScenario) ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--text-ter)]" />
        ) : (
          <Layers className="h-4 w-4 shrink-0 text-[var(--text-ter)]" />
        )}
        <span className="flex-1 min-w-0 flex flex-col">
          <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate leading-5">
            {triggerLabel}
          </span>
          <BindingSubline scenario={activeScenario} cycles={cycles} loading={loading} />
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-[var(--text-ter)] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {createPortal(popover, document.body)}
    </>
  );
}
